import { t } from "@lingui/macro";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

import { ScorePreview } from "./score-preview";

type FindingPriority = "high" | "medium" | "low" | "info";
type FindingType = "strength" | "weakness";

type Finding = {
  type: FindingType;
  priority: FindingPriority;
  text: string;
  suggestion?: string;
};

type SectionReview = {
  score: number;
  findings: Finding[];
};

type ExperienceReview = {
  title: string;
  score: number;
  findings: Finding[];
};

type TargetAlignment = {
  matchedKeywords: string[];
  missingKeywords: string[];
  alignmentNote: string;
};

export type FullReviewData = {
  score: number;
  summary: string;
  sections: {
    headline: SectionReview;
    about: SectionReview;
    experiences: ExperienceReview[];
  };
  targetAlignment?: TargetAlignment;
  topActions: string[];
};

type Props = {
  review: FullReviewData;
  ctaSlot?: ReactNode;
};

const priorityLabel = (priority: FindingPriority): string => {
  if (priority === "high") return t`High`;
  if (priority === "medium") return t`Medium`;
  if (priority === "low") return t`Low`;
  return t`Info`;
};

const priorityBadge: Record<FindingPriority, string> = {
  high: cn("border-error/30 bg-error/10 text-error"),
  medium: cn("border-warning/30 bg-warning-soft text-warning-strong"),
  low: cn("border-border bg-muted/60 text-muted-foreground"),
  info: cn("border-primary-soft-border bg-primary-soft text-primary-soft-foreground"),
};

const typeBadge: Record<FindingType, string> = {
  strength: cn("border-success/30 bg-success-soft text-success-strong"),
  weakness: cn("border-error/30 bg-error/10 text-error"),
};

const SectionFindings = ({ findings }: { findings: Finding[] }) => (
  <div className="space-y-3">
    {findings.map((finding, idx) => (
      <div key={idx} className="space-y-2">
        <div className="flex flex-wrap items-start gap-2">
          <span
            className={cn(
              "rounded border px-1.5 py-0.5 text-xs font-medium",
              typeBadge[finding.type],
            )}
          >
            {finding.type === "strength" ? t`Strength` : t`Weakness`}
          </span>
          <span
            className={cn("rounded border px-1.5 py-0.5 text-xs", priorityBadge[finding.priority])}
          >
            {priorityLabel(finding.priority)}
          </span>
          <p className="flex-1 text-sm leading-relaxed text-foreground/90">{finding.text}</p>
        </div>
        {finding.suggestion && (
          <div className="border-l-4 border-indigo-500 bg-indigo-500/10 p-3 dark:bg-indigo-950/30">
            <p className="mb-1 text-xs font-medium text-primary-soft-foreground">{t`Suggested rewrite`}</p>
            <p className="text-sm italic text-foreground/80">{finding.suggestion}</p>
          </div>
        )}
      </div>
    ))}
  </div>
);

const sectionScoreColor = (score: number): string => {
  if (score >= 70) return "text-success-strong";
  if (score >= 50) return "text-warning-strong";
  return "text-error";
};

const SectionCard = ({
  title,
  score,
  findings,
}: {
  title: string;
  score: number;
  findings: Finding[];
}) => {
  const scoreColor = sectionScoreColor(score);
  return (
    <div className="space-y-3 rounded-xl border p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        <span className={cn("text-sm font-bold", scoreColor)}>{score}/100</span>
      </div>
      <SectionFindings findings={findings} />
    </div>
  );
};

export const FullReviewResults = ({ review, ctaSlot }: Props) => (
  <div className="space-y-6">
    {/* Overall score */}
    <ScorePreview score={review.score} summary={review.summary} />

    {/* Section reviews */}
    <div className="space-y-4">
      <h2 className="text-lg font-bold">{t`Section Breakdown`}</h2>

      <SectionCard
        title={t`Headline`}
        score={review.sections.headline.score}
        findings={review.sections.headline.findings}
      />

      <SectionCard
        title={t`About`}
        score={review.sections.about.score}
        findings={review.sections.about.findings}
      />

      {review.sections.experiences.map((exp, idx) => (
        <SectionCard key={idx} title={exp.title} score={exp.score} findings={exp.findings} />
      ))}
    </div>

    {/* Target alignment */}
    {review.targetAlignment && (
      <div className="space-y-3 rounded-xl border p-4 sm:p-6">
        <h2 className="text-lg font-bold">{t`Target Role Alignment`}</h2>
        <p className="text-sm text-muted-foreground">{review.targetAlignment.alignmentNote}</p>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t`Matched keywords`}</p>
          <div className="flex flex-wrap gap-2">
            {review.targetAlignment.matchedKeywords.map((kw) => (
              <span
                key={kw}
                className="rounded border border-success/30 bg-success-soft px-2 py-0.5 text-xs text-success-strong"
              >
                {kw}
              </span>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t`Missing keywords`}</p>
          <div className="flex flex-wrap gap-2">
            {review.targetAlignment.missingKeywords.map((kw) => (
              <span
                key={kw}
                className="rounded border border-error/30 bg-error/10 px-2 py-0.5 text-xs text-error"
              >
                {kw}
              </span>
            ))}
          </div>
        </div>
      </div>
    )}

    {/* Top actions */}
    {review.topActions.length > 0 && (
      <div className="space-y-3 rounded-xl border p-4 sm:p-6">
        <h2 className="text-lg font-bold">{t`Top Actions`}</h2>
        <ol className="space-y-2">
          {review.topActions.map((action, idx) => (
            <li key={idx} className="flex gap-3 text-sm">
              <span className="mt-0.5 font-bold text-primary">{idx + 1}.</span>
              <span className="font-medium">{action}</span>
            </li>
          ))}
        </ol>
      </div>
    )}

    {/* CTA slot */}
    {ctaSlot}
  </div>
);
